import { supabase, DEFAULT_ORG_ID, applyOrgFilter, getActiveOrgOverride } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { buildOverview, lineasAjustables, buildReporte } from '../inventory'

const resolveOrgId = async () => {
  const { data: staff } = (await getCurrentStaff()) || {}
  return getActiveOrgOverride() || staff?.org_id || DEFAULT_ORG_ID
}

export const getInventoryItems = async () => {
  try {
    let query = supabase.from('inventory_items').select('*, deporte:deportes(nombre)').eq('activo', true).order('nombre')
    query = applyOrgFilter(query)
    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getInventoryItems]', error)
    return { data: [], error }
  }
}

export const createInventoryItem = async (itemData) => {
  try {
    const orgId = await resolveOrgId()
    const { data, error } = await supabase
      .from('inventory_items')
      .insert({
        nombre: itemData.nombre,
        categoria: itemData.categoria || null,
        deporte_id: itemData.deporte_id || null,
        talla: itemData.talla || null,
        modelo: itemData.modelo || null,
        ubicacion: itemData.ubicacion || null,
        notas: itemData.notas || null,
        activo: true,
        org_id: orgId,
      })
      .select()
      .single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[createInventoryItem]', error)
    return { data: null, error }
  }
}

export const updateInventoryItem = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('inventory_items')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id).select().single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[updateInventoryItem]', error)
    return { data: null, error }
  }
}

export const deleteInventoryItem = async (id) => {
  try {
    const { data, error } = await supabase
      .from('inventory_items')
      .update({ activo: false, updated_at: new Date().toISOString() })
      .eq('id', id).select().single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[deleteInventoryItem]', error)
    return { data: null, error }
  }
}

export const registrarMovimiento = async ({ itemId, tipo, cantidad, fecha = null, motivo = null }) => {
  try {
    const orgId = await resolveOrgId()
    const { data: staff } = (await getCurrentStaff()) || {}
    // Convencion de signo: alta = +, perdida/desecho = -, ajuste = tal cual.
    const magnitude = Math.abs(Number(cantidad) || 0)
    const signed = tipo === 'alta' ? magnitude
      : (tipo === 'perdida' || tipo === 'desecho') ? -magnitude
      : Number(cantidad) || 0
    const { data, error } = await supabase
      .from('inventory_movements')
      .insert({
        item_id: itemId, tipo, cantidad: signed,
        fecha: fecha || new Date().toISOString().slice(0, 10),
        motivo: motivo || null, registrado_por: staff?.id || null, org_id: orgId,
      })
      .select().single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[registrarMovimiento]', error)
    return { data: null, error }
  }
}

export const getInventoryOverview = async () => {
  try {
    const { data: items, error: itemsErr } = await getInventoryItems()
    if (itemsErr) throw itemsErr
    let movQ = supabase.from('inventory_movements').select('item_id, cantidad')
    movQ = applyOrgFilter(movQ)
    const { data: movements, error: movErr } = await movQ
    if (movErr) throw movErr
    let loanQ = supabase.from('inventory_loans').select('item_id, cantidad, fecha_devolucion')
    loanQ = applyOrgFilter(loanQ)
    const { data: loans, error: loanErr } = await loanQ
    if (loanErr) throw loanErr
    return { data: buildOverview(items, movements || [], loans || []), error: null }
  } catch (error) {
    console.error('[getInventoryOverview]', error)
    return { data: [], error }
  }
}

export const registrarPrestamo = async ({ itemId, cantidad = 1, tipo = 'prestamo', prestadoATipo, prestadoAId = null, prestadoANombre = null, fechaPrestamo = null, notas = null }) => {
  try {
    const orgId = await resolveOrgId()
    const { data: staff } = (await getCurrentStaff()) || {}
    const { data, error } = await supabase
      .from('inventory_loans')
      .insert({
        item_id: itemId, cantidad: Number(cantidad) || 1, tipo,
        prestado_a_tipo: prestadoATipo,
        prestado_a_id: prestadoAId || null,
        prestado_a_nombre: prestadoANombre || null,
        prestado_por: staff?.id || null,
        fecha_prestamo: fechaPrestamo || new Date().toISOString().slice(0, 10),
        fecha_devolucion: null, notas: notas || null, org_id: orgId,
      })
      .select().single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[registrarPrestamo]', error)
    return { data: null, error }
  }
}

export const devolverPrestamo = async (loanId, fecha = null) => {
  try {
    const { data, error } = await supabase
      .from('inventory_loans')
      .update({ fecha_devolucion: fecha || new Date().toISOString().slice(0, 10), updated_at: new Date().toISOString() })
      .eq('id', loanId).select().single()
    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[devolverPrestamo]', error)
    return { data: null, error }
  }
}

export const getPrestamosActivos = async () => {
  try {
    let query = supabase
      .from('inventory_loans')
      .select('*, item:inventory_items(nombre, categoria, talla, modelo)')
      .is('fecha_devolucion', null)
      .order('fecha_prestamo', { ascending: false })
    query = applyOrgFilter(query)
    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getPrestamosActivos]', error)
    return { data: [], error }
  }
}

export const iniciarConteo = async ({ nombre = null } = {}) => {
  try {
    const orgId = await resolveOrgId()
    const { data: staff } = (await getCurrentStaff()) || {}
    const { data: overview, error: ovErr } = await getInventoryOverview()
    if (ovErr) throw ovErr

    const { data: count, error: countErr } = await supabase
      .from('inventory_counts')
      .insert({
        nombre: nombre || null,
        estado: 'abierto',
        creado_por: staff?.id || null,
        org_id: orgId,
      })
      .select().single()
    if (countErr) throw countErr

    const lines = (overview || []).map((item) => ({
      count_id: count.id,
      item_id: item.id,
      cantidad_esperada: item.total,
      cantidad_real: null,
      ajustar: true,
      org_id: orgId,
    }))
    if (lines.length > 0) {
      const { error: linesErr } = await supabase.from('inventory_count_lines').insert(lines)
      if (linesErr) throw linesErr
    }
    return { data: count, error: null }
  } catch (error) {
    console.error('[iniciarConteo]', error)
    return { data: null, error }
  }
}

export const getConteos = async () => {
  try {
    let query = supabase
      .from('inventory_counts')
      .select('*')
      .order('fecha', { ascending: false })
      .order('created_at', { ascending: false })
    query = applyOrgFilter(query)
    const { data, error } = await query
    if (error) throw error
    return { data: data || [], error: null }
  } catch (error) {
    console.error('[getConteos]', error)
    return { data: [], error }
  }
}

export const getConteo = async (countId) => {
  try {
    let countQuery = supabase.from('inventory_counts').select('*').eq('id', countId)
    countQuery = applyOrgFilter(countQuery)
    const { data: count, error: countErr } = await countQuery.single()
    if (countErr) throw countErr
    const { data: lines, error: linesErr } = await supabase
      .from('inventory_count_lines')
      .select('*, item:inventory_items(nombre, categoria, talla, modelo)')
      .eq('count_id', countId)
    if (linesErr) throw linesErr
    return { data: { ...count, lines: lines || [] }, error: null }
  } catch (error) {
    console.error('[getConteo]', error)
    return { data: null, error }
  }
}

export const guardarLineasConteo = async (lines = []) => {
  try {
    for (const line of lines) {
      const real = line.cantidad_real === '' || line.cantidad_real === null || line.cantidad_real === undefined
        ? null
        : Number(line.cantidad_real)
      const { error } = await supabase
        .from('inventory_count_lines')
        .update({ cantidad_real: real, ajustar: !!line.ajustar, updated_at: new Date().toISOString() })
        .eq('id', line.id)
      if (error) throw error
    }
    return { error: null }
  } catch (error) {
    console.error('[guardarLineasConteo]', error)
    return { error }
  }
}

export const cerrarConteo = async (countId) => {
  try {
    const orgId = await resolveOrgId()
    const { data: staff } = (await getCurrentStaff()) || {}

    // Guarda de doble-cierre: si el conteo ya esta cerrado, no re-aplicar ajustes.
    const { data: countRow, error: estadoErr } = await supabase
      .from('inventory_counts').select('estado').eq('id', countId).single()
    if (estadoErr) throw estadoErr
    if (countRow?.estado === 'cerrado') {
      return { data: { ajustados: 0, yaCerrado: true }, error: null }
    }

    const { data: lines, error: linesErr } = await supabase
      .from('inventory_count_lines').select('*').eq('count_id', countId)
    if (linesErr) throw linesErr

    const ajustes = lineasAjustables(lines || [])
    for (const l of ajustes) {
      const { error: movErr } = await supabase
        .from('inventory_movements')
        .insert({
          item_id: l.item_id,
          tipo: 'ajuste',
          cantidad: l.diferencia,
          motivo: 'Ajuste por conteo',
          count_id: countId,
          registrado_por: staff?.id || null,
          org_id: orgId,
        })
      if (movErr) throw movErr
    }

    const { error: closeErr } = await supabase
      .from('inventory_counts')
      .update({ estado: 'cerrado', cerrado_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq('id', countId)
      .eq('estado', 'abierto')
    if (closeErr) throw closeErr

    return { data: { ajustados: ajustes.length }, error: null }
  } catch (error) {
    console.error('[cerrarConteo]', error)
    return { data: null, error }
  }
}

export const getReporteRango = async ({ desde = null, hasta = null, deporteId = null } = {}) => {
  try {
    const { data: allItems, error: itemsErr } = await getInventoryItems()
    if (itemsErr) throw itemsErr
    // deporteId: null = todos; '__general__' = solo sin deporte; otro = ese deporte.
    const items =
      deporteId === '__general__'
        ? (allItems || []).filter((it) => !it.deporte_id)
        : deporteId
          ? (allItems || []).filter((it) => it.deporte_id === deporteId)
          : (allItems || [])

    let movQ = supabase.from('inventory_movements').select('item_id, tipo, cantidad, fecha')
    movQ = applyOrgFilter(movQ)
    const { data: movements, error: movErr } = await movQ
    if (movErr) throw movErr

    return { data: buildReporte(items, movements || [], { desde, hasta }), error: null }
  } catch (error) {
    console.error('[getReporteRango]', error)
    return { data: [], error }
  }
}
